import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Facebook, Settings } from 'lucide-react';
import { Button } from './ui/button';
import { useFacebookPage } from '../contexts/FacebookPageContext';
import { useApi } from '../lib/api';

interface FacebookPageData {
  id: string;
  name: string;
  picture?: string;
  access_token?: string;
  platform?: 'facebook' | 'instagram';
  username?: string;
  isConnected?: boolean;
}

// Route suffixes that are scoped to one page, so switching pages must rewrite
// the URL. Keep in sync with App.tsx and PageRouteGuard.
const PAGE_SCOPED_SUFFIXES = ['comments', 'moderation', 'moderation-rules', 'review', 'autoreply'];

const FacebookPageSelector: React.FC = () => {
  const {
    selectedPage,
    setSelectedPage,
    clearSelectedPage,
    isPageSelected,
    rememberedPageId,
    isRestoring,
  } = useFacebookPage();
  const [pages, setPages] = useState<FacebookPageData[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  // Page id we have already re-fetched the list for, so a selection we don't
  // recognise triggers exactly one re-check before we conclude it is stale.
  const reverifiedIdRef = useRef<string | null>(null);
  const navigate = useNavigate();
  const api = useApi();

  const loadFacebookPages = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/api/facebook/pages');
      // Handle the response structure: { pages: [...] }
      const pagesData = response.data?.pages || response.data || [];
      const list: FacebookPageData[] = Array.isArray(pagesData) ? pagesData : [];
      // That endpoint also returns disconnected pages; the switcher must only
      // ever offer pages the user can actually work on.
      setPages(list.filter((p) => p.isConnected !== false));
    } catch (error) {
      console.error('Error loading Facebook pages:', error);
      setPages([]);
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
    // 'api' omitted: useApi() returns a fresh object each render.
    // eslint-disable-next-line
  }, []);

  // Load Facebook pages
  useEffect(() => {
    loadFacebookPages();
  }, [loadFacebookPages]);

  // Reconcile the cached selection with the real list, then preselect.
  // Deliberately never navigates: auto-selecting fills in the switcher, it must
  // not teleport a user who is sitting on /settings or /accounts.
  useEffect(() => {
    if (!hasLoaded || isLoading || isRestoring) return;

    // Selection points at a page this list doesn't contain. That means either
    // our list is stale (the user just connected the page — it isn't in the
    // copy we fetched on mount) or the page really is gone. Re-fetch once
    // before judging, otherwise connecting a first page would immediately wipe
    // the selection the user just made.
    if (selectedPage && !pages.some((p) => p.id === selectedPage.id)) {
      if (reverifiedIdRef.current !== selectedPage.id) {
        reverifiedIdRef.current = selectedPage.id;
        loadFacebookPages();
        return;
      }
      console.warn('⚠️ [PAGE_SELECTOR] Cached page no longer connected, clearing:', selectedPage.id);
      clearSelectedPage();
      return;
    }

    // Nothing selected → prefer the page the server remembers, else the first.
    if (!selectedPage && pages.length > 0) {
      const target = pages.find((p) => p.id === rememberedPageId) || pages[0];
      setSelectedPage(target);
    }
    // setSelectedPage/clearSelectedPage are omitted deliberately: the context
    // recreates them every render, and including them would re-run this on
    // every render.
  }, [hasLoaded, isLoading, isRestoring, pages, selectedPage, rememberedPageId, loadFacebookPages]);

  const handlePageSelect = (page: FacebookPageData) => {
    setSelectedPage(page);
    setIsOpen(false);
    
    // Trigger a custom event to notify other components about page change
    window.dispatchEvent(new CustomEvent('facebookPageChanged', { 
      detail: { pageId: page.id, pageName: page.name } 
    }));
    
    // Update URL to reflect the selected page if we're on a page-specific route
    const currentPath = window.location.pathname;
    if (PAGE_SCOPED_SUFFIXES.some((suffix) => currentPath.includes(`/${suffix}`))) {
      const newPath = currentPath.replace(/\/accounts\/[^\/]+/, `/accounts/${page.id}`);
      navigate(newPath);
    }
  };

  // Hold the space while the list is in flight, otherwise the top bar flickers
  // through "nothing" → "Select account" → the real page in ~300ms.
  if (!hasLoaded || isLoading) {
    return <div className="h-8 w-32 rounded-md bg-gray-100 animate-pulse" />;
  }

  // No connected pages/accounts at all → render nothing in the top bar.
  if (pages.length === 0) {
    return null;
  }

  if (!isPageSelected) {
    return (
      <div className="flex items-center space-x-2">
        <Button
          onClick={() => navigate('/accounts')}
          variant="outline"
          size="sm"
          className="flex items-center space-x-2"
        >
          <Facebook className="h-4 w-4" />
          <span>Select account</span>
        </Button>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Selected Page Display */}
      <div className="flex items-center space-x-3">
        {/* Page Info */}
        <div className="flex items-center space-x-2">
          {selectedPage?.picture ? (
            <img
              src={selectedPage.picture}
              alt={selectedPage.name}
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
              <Facebook className="h-4 w-4 text-white" />
            </div>
          )}
          <div className="block min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate max-w-[120px] sm:max-w-32">
              {selectedPage?.name}
            </p>
            <p className="hidden sm:block text-xs text-gray-500">
              {selectedPage?.platform === "instagram" ? "Instagram account" : "Facebook Page"}
            </p>
          </div>
        </div>



        {/* Page Selector Dropdown */}
        <div className="relative">
          <Button
            onClick={() => setIsOpen(!isOpen)}
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            title="Change Facebook Page"
          >
            <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </Button>

          {/* Dropdown Menu */}
          {isOpen && (
            <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
              <div className="p-2">
                <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Select Facebook Page
                </div>
                {isLoading ? (
                  <div className="px-3 py-2 text-sm text-gray-500">Loading pages...</div>
                ) : !Array.isArray(pages) || pages.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500">No pages available</div>
                ) : (
                  <div className="space-y-1">
                    {pages.map((page) => (
                      <button
                        key={page.id}
                        onClick={() => handlePageSelect(page)}
                                                 className={`w-full flex items-center space-x-3 px-3 py-2 text-sm rounded-md transition-colors ${
                           selectedPage?.id === page.id
                             ? 'bg-blue-50 text-blue-700'
                             : 'text-gray-700 hover:bg-gray-50'
                         }`}
                      >
                        {page.picture ? (
                          <img
                            src={page.picture}
                            alt={page.name}
                            className="w-6 h-6 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                            <Facebook className="h-3 w-3 text-white" />
                          </div>
                        )}
                        <span className="truncate">{page.name}</span>
                      </button>
                    ))}
                  </div>
                )}
                <div className="border-t border-gray-200 mt-2 pt-2">
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      navigate('/accounts');
                    }}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md"
                  >
                    <Settings className="h-4 w-4" />
                    <span>Manage Pages</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Click outside to close dropdown */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default FacebookPageSelector; 